import { LosseBlogBink, LosseLink, LossePlaatjie } from '@ubo/losse-sjedel'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import Content from '~/components/elements/Content'
import type { Page_Flexcontent_Flex_Posts, Post } from '~/graphql/types'

const DropdownIcon = ({ className = 'pointer-events-none absolute right-6 top-1/2 -translate-y-1/2' }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
    <path
      d="M6.707 7.293C6.44439 7.29346 6.18428 7.24201 5.94162 7.14162C5.69896 7.04122 5.47853 6.89386 5.293 6.708L0 1.414L1.414 0L6.707 5.293L12 0L13.414 1.414L8.121 6.707C7.93556 6.89304 7.71516 7.04059 7.4725 7.14115C7.22983 7.24172 6.96968 7.29333 6.707 7.293Z"
      fill="black"
      fillOpacity="0.35"
    />
  </svg>
)

export default function Cases({ fields }: { fields: Page_Flexcontent_Flex_Posts }) {
  const [branchFilter, setBranchFilter] = useState<number | 'all'>('all')
  const [serviceFilter, setServiceFilter] = useState<number | 'all'>('all')

  const categoryPosts = useMemo(() => {
    const postEdges = fields.posts?.edges ?? []
    return postEdges.filter((edge) => {
      const post = edge?.node as Post

      return post.categories?.edges?.some((category) => category?.node?.databaseId === 87)
    })
  }, [fields.posts])

  const branchOptions = useMemo(() => {
    const map = new Map<number, string>()

    categoryPosts?.forEach((edge) => {
      const post = edge?.node as Post

      post.categories?.edges
        ?.filter((category) => category?.node?.parentDatabaseId === 90)
        ?.forEach((category) => {
          if (category?.node?.databaseId && category.node.name) {
            map.set(category.node.databaseId, category.node.name)
          }
        })
    })

    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [categoryPosts])

  const serviceOptions = useMemo(() => {
    const map = new Map<number, string>()

    categoryPosts?.forEach((edge) => {
      const post = edge?.node as Post

      post.categories?.edges
        ?.filter((category) => category?.node?.parentDatabaseId === 91)
        ?.forEach((category) => {
          if (category?.node?.databaseId && category.node.name) {
            map.set(category.node.databaseId, category.node.name)
          }
        })
    })

    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [categoryPosts])

  const filteredPosts = useMemo(() => {
    return categoryPosts?.filter((edge) => {
      const post = edge?.node as Post

      const branchIds =
        post.categories?.edges
          ?.filter((category) => category?.node?.parentDatabaseId === 90)
          ?.map((category) => category?.node?.databaseId)
          ?.filter((id): id is number => typeof id === 'number') || []

      const serviceIds =
        post.categories?.edges
          ?.filter((category) => category?.node?.parentDatabaseId === 91)
          ?.map((category) => category?.node?.databaseId)
          ?.filter((id): id is number => typeof id === 'number') || []

      const branchMatches = branchFilter === 'all' || branchIds.includes(branchFilter)
      const serviceMatches = serviceFilter === 'all' || serviceIds.includes(serviceFilter)

      return branchMatches && serviceMatches
    })
  }, [branchFilter, categoryPosts, serviceFilter])

  const getCategoryNamesByParent = (post: Post, parentId: number) =>
    post.categories?.edges
      ?.filter((category) => category?.node?.parentDatabaseId === parentId)
      ?.map((category) => category?.node?.name)
      ?.filter((name): name is string => Boolean(name))

  if (!fields.posts) {
    return null
  }

  return (
    <section id="Cases">
      <LosseBlogBink>
        <div className="container-right lg:pb-48 pb-24">
          <div className="flex flex-col gap-12">
            <div className="mb-6">
              {(branchOptions.length > 0 || serviceOptions.length > 0) && (
                <div className="flex flex-col md:flex-row gap-4 md:items-end">
                  {branchOptions.length > 0 && (
                    <div className="relative">
                      <select
                        id="branches"
                        value={branchFilter === 'all' ? 'all' : branchFilter}
                        onChange={(event) => {
                          const value = event.target.value
                          setBranchFilter(value === 'all' ? 'all' : Number(value))
                        }}
                        className="px-8 py-4 pr-12 lg:min-w-[400px] w-full bg-metric-zangoose text-lg appearance-none"
                      >
                        <option value="all">Filter op branches</option>
                        {branchOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <DropdownIcon />
                    </div>
                  )}
                  {serviceOptions.length > 0 && (
                    <div className="relative">
                      <select
                        id="services"
                        value={serviceFilter === 'all' ? 'all' : serviceFilter}
                        onChange={(event) => {
                          const value = event.target.value
                          setServiceFilter(value === 'all' ? 'all' : Number(value))
                        }}
                        className="px-8 py-4 pr-12 lg:min-w-[400px] w-full bg-metric-zangoose text-lg appearance-none"
                      >
                        <option value="all">Filter op diensten</option>
                        {serviceOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <DropdownIcon />
                    </div>
                  )}
                </div>
              )}
            </div>
            {filteredPosts?.length ? (
              filteredPosts.map((edge, index) => {
                const post = edge?.node as Post
                const branchNames = getCategoryNamesByParent(post, 90)
                const serviceNames = getCategoryNamesByParent(post, 91)

                return (
                  <motion.div key={index}>
                    <LosseLink
                      className="grid xl:grid-cols-4 md:grid-cols-5 items-center gap-10 pb-12 border-b border-metric-qwilfish"
                      to={post.uri || '/'}
                    >
                      <div className="xl:col-span-1 md:col-span-2">
                        <div className="relative overflow-hidden">
                          {post.recap?.companyLogoCase && (
                            <div className="absolute inset-0 bg-metric-cinderace/50 flex items-center justify-center">
                              <LossePlaatjie className="w-1/2 h-auto mx-auto" src={post.recap?.companyLogoCase} maxwidth={500} />
                            </div>
                          )}
                          {post.recap?.highlightedImage ? (
                            <LossePlaatjie
                              className="lg:aspect-[42/27] max-lg:w-full max-md:h-72 max-sm:h-52 object-cover"
                              src={post.recap?.highlightedImage}
                              maxwidth={500}
                            />
                          ) : (
                            <div className="bg-metric-cinderace lg:aspect-[42/27] block w-full h-full" />
                          )}
                        </div>
                        <div className="flex flex-col gap-1 bg-metric-qwilfish p-6 text-white">
                          <div className="font-bold lg:text-xl text-lg">{post.recap?.companyCase}</div>
                          <div className="lg:text-base text-sm">{post.recap?.branchCase}</div>
                        </div>
                      </div>
                      <div className="md:col-span-3 lg:max-w-[700px] text-metric-cinderace">
                        {branchNames?.length ? (
                          <div className="bg-metric-hariyama py-2 px-5 text-white w-fit lg:mb-8 mb-6">{branchNames.join(', ')}</div>
                        ) : null}
                        <h2 className="uppercase font-bold lg:text-2xl text-xl">{post.title}</h2>
                        <Content className="mt-3 children-p:leading-[30px]">{post.recap?.excerpt}</Content>
                        {serviceNames?.length ? (
                          <div className="mt-8 font-bold">
                            <span className="mr-2">Dienst:</span>
                            {serviceNames.map((name, idx) => (
                              <span key={name}>
                                {idx > 0 && ', '}
                                <LosseLink
                                  to={`/aanbod/diensten/${name
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')
                                    .replace(/[^a-z0-9\-]/g, '')}/`}
                                  className="hover:underline hover:text-metric-hariyama"
                                >
                                  {name}
                                </LosseLink>
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </LosseLink>
                  </motion.div>
                )
              })
            ) : (
              <p className="text-metric-cinderace uppercase mt-10 font-bold text-2xl">Geen resultaten gevonden</p>
            )}
          </div>
        </div>
      </LosseBlogBink>
    </section>
  )
}
